/**
 * Cuentas del backoffice (ADM-01).
 *
 * Todos los administradores son iguales: no hay roles ni niveles. Por eso la pantalla no tiene selector de
 * permisos, solo alta, baja y a quien pertenece cada cuenta.
 *
 * La lista ocupa el ancho completo y el alta vive en una ventana modal: dar de
 * alta a alguien pasa tres veces en la vida del proyecto —son hasta tres
 * cuentas—, mientras que revisar quien tiene acceso es lo que se hace siempre.
 * Un formulario permanente al lado le robaria la mitad del ancho a la tabla
 * para algo que casi nunca se usa.
 *
 * Hay dos formas de sacar a alguien y no son la misma: desactivar es reversible
 * —sirve para quien se va un mes o queda en duda— y eliminar borra la cuenta.
 * Como el tope de cuentas activas es tres, dejar acumuladas las desactivadas
 * termina volviendo la lista ilegible, y por eso existen las dos.
 *
 * La clave nunca se muestra aca: la genera el servidor y viaja por correo. Si
 * apareciera en esta pantalla, terminaria dictada por WhatsApp.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';

import type { Administrador } from '@bandu/compartido';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { Modal } from '../../componentes/administracion/Modal';
import { PanelAdmin } from '../../componentes/administracion/PanelAdmin';
import { Tabla } from '../../componentes/administracion/Tabla';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { useSesion } from '../../contexto/sesion-contexto';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import {
  cambiarEstadoAdministrador,
  crearAdministrador,
  eliminarAdministrador,
  obtenerAdministradores,
} from '../../servicios/autenticacion.servicio';
import { fechaHora } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { rutas } from '../../utilidades/rutas';

/** Lo comprometido en el alcance firmado: hasta tres cuentas activas. */
const MAXIMO_ACTIVOS = 3;

export function Administradores() {
  const { administrador: propio } = useSesion();

  // Cambia despues de cada alta o baja y obliga al hook a pedir la lista otra vez.
  const [version, setVersion] = useState(0);
  const { estado, datos, error } = usePeticion(
    (senal) => obtenerAdministradores(senal),
    [version],
  );

  const [abierto, setAbierto] = useState(false);
  // Cuenta que se esta por eliminar; `null` con el modal de confirmacion cerrado.
  const [porEliminar, setPorEliminar] = useState<Administrador | null>(null);
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [falla, setFalla] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const cuentas: Administrador[] = datos ?? [];
  const activos = cuentas.filter((cuenta) => cuenta.activo).length;
  const sinCupo = activos >= MAXIMO_ACTIVOS;

  function abrirAlta(): void {
    setNombre('');
    setEmail('');
    setFalla(null);
    setAviso(null);
    setAbierto(true);
  }

  function cerrarAlta(): void {
    // Un envio a medio camino no se puede cancelar sin dejar la pantalla
    // mintiendo sobre si la cuenta se creo o no.
    if (enviando) return;
    setAbierto(false);
  }

  async function invitar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setFalla(null);
    setEnviando(true);

    try {
      const creado = await crearAdministrador({ nombre, email });
      setAbierto(false);
      // El aviso se muestra en la pagina, no en el modal: el modal ya se cerro
      // y la confirmacion tiene que quedar donde esta la lista que cambio.
      setAviso(`Cuenta creada. Le mandamos la clave a ${creado.email}.`);
      setVersion((valor) => valor + 1);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setEnviando(false);
    }
  }

  async function alternar(cuenta: Administrador): Promise<void> {
    setAviso(null);
    setFalla(null);

    try {
      await cambiarEstadoAdministrador(cuenta.id, !cuenta.activo);
      setVersion((valor) => valor + 1);
    } catch (fallo) {
      setAviso(null);
      setFalla(mensajeDe(fallo));
    }
  }

  async function eliminar(): Promise<void> {
    const cuenta = porEliminar;
    if (cuenta === null) return;

    setFalla(null);
    setEnviando(true);

    try {
      await eliminarAdministrador(cuenta.id);
      setPorEliminar(null);
      setAviso(`Se elimino la cuenta de ${cuenta.nombre}.`);
      setVersion((valor) => valor + 1);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <Seo
        titulo="Administradores"
        descripcion="Cuentas con acceso al panel."
        ruta={rutas.administracion.administradores()}
        sinIndexar
      />

      <BarraAdmin
        titulo="Administradores"
        bajada={`${activos} de ${MAXIMO_ACTIVOS} cuentas activas`}
        acciones={
          <button
            type="button"
            className="btn btn-sm btn-primario"
            onClick={abrirAlta}
            disabled={sinCupo}
            title={
              sinCupo
                ? `El panel admite hasta ${MAXIMO_ACTIVOS} cuentas activas. Desactiva una para crear otra.`
                : undefined
            }
          >
            <Icono nombre="mas" />
            Agregar administrador
          </button>
        }
      />

      <div className="admin-cuerpo">
        {estado === 'cargando' ? <Cargando que="las cuentas" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {/* Fuera del modal: son el resultado de lo que se hizo en la lista. */}
        {aviso !== null ? (
          <p className="acceso-aviso" role="status">
            <Icono nombre="checkCirc" />
            <span>{aviso}</span>
          </p>
        ) : null}

        {/* Solo con los dos modales cerrados: si no, el mismo error se veria dos veces. */}
        {falla !== null && !abierto && porEliminar === null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        {datos !== null ? (
          <PanelAdmin
            titulo="Cuentas con acceso"
            bajada="Todas tienen los mismos permisos dentro del panel"
            variante="plano"
          >
            <Tabla
              titulo="Administradores del panel"
              encabezados={
                <>
                  <th scope="col">Persona</th>
                  <th scope="col">Ultimo acceso</th>
                  <th scope="col">Alta</th>
                  <th scope="col">Estado</th>
                  <th scope="col" className="der">
                    Accion
                  </th>
                </>
              }
            >
              {cuentas.map((cuenta) => (
                <tr key={cuenta.id}>
                  <th scope="row">
                    {cuenta.nombre}
                    <span className="celda-sub">{cuenta.email}</span>
                  </th>
                  <td>
                    {cuenta.ultimoAcceso !== null ? (
                      <time dateTime={cuenta.ultimoAcceso}>{fechaHora(cuenta.ultimoAcceso)}</time>
                    ) : (
                      <span className="celda-sub">Nunca entro</span>
                    )}
                  </td>
                  <td>
                    <time dateTime={cuenta.creadoEn}>{fechaHora(cuenta.creadoEn)}</time>
                  </td>
                  <td>
                    <span className="etiquetas">
                      {cuenta.activo ? (
                        <span className="etq etq-ok">Activa</span>
                      ) : (
                        <span className="etq etq-neutra">Desactivada</span>
                      )}
                      {cuenta.debeCambiarClave ? (
                        <span className="etq etq-alerta">Clave temporal</span>
                      ) : null}
                    </span>
                  </td>
                  <td className="der">
                    {cuenta.id === propio?.id ? (
                      <span className="celda-sub">Eres tu</span>
                    ) : (
                      <span className="acciones-fila">
                        <button
                          type="button"
                          className="btn btn-xs btn-contorno"
                          onClick={() => {
                            void alternar(cuenta);
                          }}
                        >
                          {cuenta.activo ? 'Desactivar' : 'Reactivar'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-peligro"
                          onClick={() => {
                            setAviso(null);
                            setFalla(null);
                            setPorEliminar(cuenta);
                          }}
                        >
                          Eliminar
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </Tabla>
          </PanelAdmin>
        ) : null}
      </div>

      <Modal
        titulo="Dar acceso a alguien mas"
        bajada="Le llega un correo con su clave temporal"
        abierto={abierto}
        onCerrar={cerrarAlta}
      >
        <form
          className="formulario-admin"
          onSubmit={(evento) => {
            void invitar(evento);
          }}
          noValidate
        >
          {falla !== null ? (
            <p className="acceso-error" role="alert">
              <Icono nombre="alerta" />
              <span>{falla}</span>
            </p>
          ) : null}

          <div className="campo">
            <label htmlFor="nuevo-nombre">Nombre</label>
            <input
              id="nuevo-nombre"
              required
              autoComplete="off"
              value={nombre}
              onChange={(evento) => {
                setNombre(evento.target.value);
              }}
            />
          </div>

          <div className="campo">
            <label htmlFor="nuevo-email">Correo</label>
            <input
              id="nuevo-email"
              type="email"
              required
              autoComplete="off"
              aria-describedby="ayuda-correo"
              value={email}
              onChange={(evento) => {
                setEmail(evento.target.value);
              }}
            />
            <p className="campo-ayuda" id="ayuda-correo">
              A esta direccion llega la clave, asi que tiene que ser una casilla que la
              persona lea.
            </p>
          </div>

          <div className="modal-acciones">
            <button
              type="button"
              className="btn btn-contorno"
              onClick={cerrarAlta}
              disabled={enviando}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn-primario" disabled={enviando}>
              {enviando ? 'Creando…' : 'Crear la cuenta'}
            </button>
          </div>
        </form>
      </Modal>

      {/*
        Eliminar no tiene vuelta atras, asi que pasa por una confirmacion que
        nombra a quien se va a borrar. No se usa el `confirm()` del navegador:
        bloquea la pestaña entera y no se puede escribir en el.
      */}
      <Modal
        titulo="Eliminar esta cuenta"
        bajada="No se puede deshacer"
        abierto={porEliminar !== null}
        onCerrar={() => {
          if (!enviando) setPorEliminar(null);
        }}
      >
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        <p className="parrafo-panel">
          <strong>{porEliminar?.nombre}</strong> ({porEliminar?.email}) va a perder el acceso
          al panel para siempre. Si solo quieres sacarlo un tiempo, desactiva la cuenta en vez
          de eliminarla.
        </p>

        <div className="modal-acciones">
          <button
            type="button"
            className="btn btn-contorno"
            onClick={() => {
              setPorEliminar(null);
            }}
            disabled={enviando}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-peligro"
            onClick={() => {
              void eliminar();
            }}
            disabled={enviando}
          >
            {enviando ? 'Eliminando…' : 'Eliminar la cuenta'}
          </button>
        </div>
      </Modal>
    </>
  );
}
